import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { endpoints } from 'src/app/core/config/endpoints';
import { ConnectionService } from 'src/app/core/connection/connection.service';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { HttpStatusCode } from 'src/app/core/models/http-codes.enum';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { environment } from 'src/environments/environment';

@Injectable({
     providedIn: 'root'
})
export class RequestQueueService {

     hasFailedRequests$ = new BehaviorSubject<boolean>(false);
     isReplayingFailedRequests$ = new BehaviorSubject<boolean>(false);

     constructor(
          private http: HttpClient,
          private storage: LocalStorageService,
          private connectionService: ConnectionService,
          private logger: LoggerService,
          private dateHelperService: DateHelperService,
          private alertController: AlertController,
          private dateHelper: DateHelperService,
     ) { }

     async handleRequest<T>(type: string, endpoint: string, body: any, options: {}, c?: new () => T): Promise<T> {
          if (!this.connectionService.isOnline$.value) {
               await this.pushFailedRequest(type, endpoint, body, options);
               return Promise.resolve<T>(null);
          }

          if (await this.hasFailedRequests()) {
               await this.pushFailedRequest(type, endpoint, body, options);
               await this.replayFailedRequests();
               return Promise.resolve<T>(null);
          }

          try {
               return await this.executeRequestForType<T>(type, endpoint, body, options);
          } catch (error) {
               await this.pushFailedRequest(type, endpoint, body, options);
               return Promise.resolve<T>(null);
          }
     }

     async replayFailedRequests(): Promise<void> {
          if (!this.hasFailedRequests$.value) {
               this.hasFailedRequests$.next(true);
          }

          if (this.isReplayingFailedRequests$.value) {
               return;
          }
          this.logger.logTrace('Replaying failed requests');
          this.isReplayingFailedRequests$.next(true);

          const fatalErrorCodes = [
               HttpStatusCode.BadRequest,
               HttpStatusCode.ServerError,
          ];
          const failedRequests = await this.storage.failedRequests.getAllSortedBy('timestamp');
          // tslint:disable-next-line: prefer-for-of
          for (let index = 0; index < failedRequests.length; index++) {
               const req = failedRequests[index];
               try {
                    await this.executeRequestForType<any>(req.type, req.url, req.body, req.options);
                    await this.storage.failedRequests.removeItem(req.timestamp);
               } catch (error) {
                    this.logger.logException(error);
                    this.logger.logTrace(`Request failed: `, req);

                    if (fatalErrorCodes.includes(error.status)) {
                         const alert = await this.alertController.create({
                              header: 'Fatal Error',
                              message: 'An error has occurred. Please contact Mapcom Support.',
                              buttons: ['OK'],
                         });

                         await alert.present();
                         await alert.onDidDismiss();

                         this.sendFailedRequests();
                         return this.isReplayingFailedRequests$.next(false);
                    }

                    if (error.status == HttpStatusCode.Conflict) {  // LOC-1132
                         const alert = await this.alertController.create({
                              header: 'Conflict Error',
                              message: error.error.error + ' Request will be abandoned.',
                              buttons: ['OK'],
                         });

                         await alert.present();
                         await alert.onDidDismiss();
                         await this.storage.failedRequests.removeItem(req.timestamp);

                    }

               }
          }
          this.isReplayingFailedRequests$.next(false);

          if (this.hasFailedRequests$.value) {
               this.hasFailedRequests$.next(false);
          }
     }

     private async executeRequestForType<T>(type: string, endpoint: string, body: any, options: {}): Promise<T> {
          switch (type) {
               case 'POST':
                    return this.http.post<T>(endpoint, body, options).toPromise();
               case 'PUT':
                    return this.http.put<T>(endpoint, body, options).toPromise();
               case 'PATCH':
                    return this.http.patch<T>(endpoint, body, options).toPromise();
               case 'DELETE':
                    return this.http.request<T>('delete', endpoint, { body }).toPromise();
               default:
                    this.logger.logException(new Error(`Invalid HTTP Verb ${type} for endpoint ${endpoint}.`));
          }
     }

     async sendFailedRequests(): Promise<void> {
          const url = environment.apiUrl + endpoints.customerService.failedRequests;

          const postData = {
               failedRequests: (await this.storage.failedRequests.getAll()).map(f => JSON.stringify(f)),
               timestamp: this.dateHelper.getNowISO(),
               url: window.location.href,
          };

          this.http.post(url, postData).subscribe();
     }

     private async pushFailedRequest(type: string, url: string, body: any, options: {}) {
          if (!this.hasFailedRequests$.value) {
               this.hasFailedRequests$.next(true);
          }

          const timestamp = this.dateHelperService.getNowInMilliseconds().toString();
          const failedRequest = { timestamp, type, url, body, options };
          await this.storage.failedRequests.setItem(timestamp, failedRequest);
          this.logger.logTrace(`Pushed new request into failed requests queue. Request: `, failedRequest);
     }

     async alertFailedRequests() {
          const failedRequestsAlert = await this.alertController.create({
               header: 'Offline Requests Syncing',
               message: 'We\'re currently syncing requests that were made offline. Please try to sync again.',
               buttons: ['OK'],
          });

          failedRequestsAlert.present();
     }

     async hasFailedRequests(): Promise<boolean> {
          const failedEntries = await this.storage.failedRequests.length();

          return failedEntries > 0;
     }

     async clearFailedRequests(): Promise<void> {
          this.hasFailedRequests$.next(false);
          await this.storage.failedRequests.clear();
     }

}
